<template>
    <v-dialog
        :value="isOpen"
        scrollable
        max-width="750px"
        transition="slide-y-transition"
        origin="top center"
        eager
        @keydown.esc="onClose"
        @click:outside="onClose"
    >
        <v-card tile>
            <v-card-title>
                <v-row>
                    <v-col cols="12">
                        <h1 class="text-subtitle-1">
                            <template>
                                Dodaj poprawione pliki produkcyjne
                            </template>
                        </h1>
                    </v-col>
                    <v-col cols="12">
                        <v-alert
                            v-if="orderProduct.orderProductAttachment.length > 0"
                            type="error"
                            dense
                            outlined
                        >
                            Aby dodać nowy plik najpierw usuń istniejący z listy poniżej.
                        </v-alert>
                    </v-col>
                </v-row>
            </v-card-title>
            <v-card-text>
                <v-row dense>
                    <v-col cols="12">
                        <v-card :loading="actionLoading.fileRemoving" loader-height="3">
                            <v-overlay
                                color="white"
                                opacity="0.85"
                                absolute
                                :value="actionLoading.fileUploading"
                            >
                                <v-container class="pa-0">
                                    <v-row justify="center">
                                        <v-col cols="auto">
                                            <v-progress-circular
                                                :rotate="-90"
                                                :size="120"
                                                :width="4"
                                                :value="uploadingProgressValue"
                                                color="primary"
                                            >
                                                {{ uploadingProgressValue }}
                                            </v-progress-circular>
                                        </v-col>
                                    </v-row>
                                    <v-row justify="center">
                                        <v-col cols="auto">
                                            <v-btn
                                                color="error"
                                                small
                                                outlined
                                                data-sentry-label="Anuluj"
                                                @click="abortUploadingProductionFileAttachment"
                                            >
                                                Anuluj
                                            </v-btn>
                                        </v-col>
                                    </v-row>
                                </v-container>
                            </v-overlay>
                            <v-card-text>
                                <v-container class="pa-0">
                                    <v-row>
                                        <v-col cols="auto" sm="8" md="6" lg="8">
                                            <v-file-input
                                                v-model="currentProductionFile"
                                                label="Plik produkcyjny"
                                                counter
                                                placeholder="Wybierz plik"
                                                prepend-icon="mdi-paperclip"
                                                outlined
                                                dense
                                                :show-size="1000"
                                                :rules="currentProductionFileRules"
                                                :error-messages="currentProductionFileErrors"
                                            >
                                                <template #selection="{index, text}">
                                                    <v-chip
                                                        v-if="index < 2"
                                                        label
                                                        small
                                                    >
                                                        {{ text }}
                                                    </v-chip>
                                                </template>
                                            </v-file-input>
                                        </v-col>
                                        <v-col cols="auto">
                                            <v-btn
                                                color="primary"
                                                outlined
                                                data-sentry-label="Wyślij plik"
                                                :disabled="!canUploadFile()"
                                                @click="addProductionFileAttachment"
                                            >
                                                <v-icon left>
                                                    mdi-file-upload
                                                </v-icon>
                                                Wyślij
                                            </v-btn>
                                        </v-col>
                                    </v-row>
                                </v-container>

                                <v-data-table
                                    v-if="orderProduct.orderProductAttachment.length > 0"
                                    :items="orderProduct.orderProductAttachment"
                                    :headers="headers"
                                    disable-pagination
                                    hide-default-footer
                                    :mobile-breakpoint="0"
                                >
                                    <template #item.fileName="{item}">
                                        {{ productionFileName(item.file) }}
                                    </template>
                                    <template #item.actions="{item}">
                                        <div class="d-inline-flex">
                                            <v-tooltip :open-delay="tooltipDelay" top>
                                                <template #activator="{on}">
                                                    <v-btn
                                                        icon
                                                        data-sentry-label="Usuń załącznik"
                                                        v-on="on"
                                                        @click="deleteProductionFileAttachment(item.id)"
                                                    >
                                                        <v-icon color="pink">
                                                            mdi-delete
                                                        </v-icon>
                                                    </v-btn>
                                                </template>
                                                <span>Usuń</span>
                                            </v-tooltip>
                                        </div>
                                    </template>
                                </v-data-table>

                                <v-alert
                                    v-else
                                    type="info"
                                    text
                                    border="left"
                                >
                                    Nie dodano pliku produkcyjnego.
                                </v-alert>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
            </v-card-text>
            <v-divider />
            <v-card-actions>
                <v-btn
                    color="error"
                    text
                    data-sentry-label="Zamknij"
                    @click="onClose"
                >
                    Zamknij
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>
<script>
import tooltipDelay from '@/mixins/tooltip'
import { errorHandler } from '@/utils/errorHandler'
import { appConfig } from '@/config'
import { pdfFileName } from '@/utils/customValidators'

const maxFileSize = (value) => {
    return value.size < appConfig.orders.productionFileAttachmentSize * 1024 * 1024
}

export default {
    name: 'OrderProductAttachmentDialog',
    mixins: [
        tooltipDelay
    ],
    props: {
        isOpen: {
            type: Boolean
        },
        orderProductData: {
            type: Object,
            default: () => {}
        }
    },
    data() {
        return {
            orderProduct: {
                orderProductAttachment: []
            },
            currentProductionFile: null,
            actionLoading: {
                fileUploading: false,
                fileRemoving: false
            },
            headers: [
                {
                    text: 'Nazwa pliku',
                    value: 'fileName',
                    align: 'left',
                    sortable: false,
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    align: 'center',
                    sortable: false,
                    width: 100
                },
            ],
            currentProductionFileRules: [
                value => !!(this.currentProductionFile && (maxFileSize(value)) ||
                    `Przekroczono dopuszczalny rozmiar pliku: ${appConfig.orders.productionFileAttachmentSize}MB.`),
                value => !!(this.currentProductionFile && (pdfFileName(value?.name)) ||
                    'Nazwa pliku zawiera niedozwolne znaki - /:\'"?*<>|()[]%$#@{}~^&.'),
            ],
        }
    },
    computed: {
        uploadingProgressValue() {
            return this.$store.state.common.progress.value
        },
        currentProductionFileErrors() {
            if (!this.currentProductionFile) return

            const errors = []

            if (!this.$v.currentProductionFile.$dirty) return errors
            this.currentProductionFileRules.map(e => errors.push(e))

            return errors
        },
    },
    validations() {
        const rules = {}

        if (this.currentProductionFile) {
            rules['currentProductionFile'] = {
                maxFileSize,
                fileName: value => pdfFileName(value.name)
            }
        }

        return rules
    },
    watch: {
        isOpen(boolean) {
            if (boolean) {
                this.orderProduct = JSON.parse(JSON.stringify(this.orderProductData))
            } else {
                setTimeout(() => {
                    this.orderProduct = {
                        orderProductAttachment: []
                    }
                }, 500)
            }
        }
    },
    methods: {
        canUploadFile() {
            return this.currentProductionFile && !this.$v.currentProductionFile.$invalid
        },
        addProductionFileAttachment() {
            this.$v.currentProductionFile.$touch()

            if (this.$v.currentProductionFile.$invalid) return

            const formData = new FormData()

            formData.append('file', this.currentProductionFile)
            formData.append('productId', this.orderProduct.id)

            this.actionLoading.fileUploading = true

            this.$store.dispatch('orders/addProductionFileAttachment', {
                formData: formData,
                abort: false
            })
                .then(response => {
                    this.orderProduct.orderProductAttachment.push(response.data)
                    this.currentProductionFile = null
                    this.$emit('on-save', response.data)
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.fileUploading = false
                })
        },
        abortUploadingProductionFileAttachment() {
            this.$store.dispatch('orders/addProductionFileAttachment', {
                formData: null,
                abort: true
            })
                .catch(error => {
                    errorHandler(error)
                })
        },
        deleteProductionFileAttachment(id) {
            this.actionLoading.fileRemoving = true
            this.$store.dispatch('orders/deleteProductionFileAttachment', id)
                .then(() => {
                    const index = this.orderProduct.orderProductAttachment.findIndex(item => item.id == id)

                    this.orderProduct.orderProductAttachment.splice(index, 1)
                    this.$emit('on-delete', index)

                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.fileRemoving = false
                })
        },
        productionFileName(fileName) {
            const words = fileName.split('/')

            return words[words.length-1]
        },
        onClose() {
            this.$emit('on-close')
            setTimeout(() => {
                this.orderProduct = {
                    orderProductAttachment: []
                }
            }, 500)
        }
    }
}
</script>